import {
  faAddressCard,
  faClock,
  faCookieBite,
  faDownload,
  faEnvelope,
  faEye,
  faGear,
  faImage,
  faKey,
  faPause,
  faRotate,
  faShieldHalved,
  faTrashCan,
  faUser,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  type ChangeEvent,
  type FormEvent,
  useCallback,
  useEffect,
  useState,
} from "react";
import React from "react";
import { Navigate, useNavigate } from "react-router-dom";

import { api, ApiClientError } from "../api";
import { useAuth } from "../auth";
import { notifyProfileImageChanged } from "../profile-image";
import { useDialogFocusTrap } from "../components/useDialogFocusTrap";

interface Account {
  firstName: string;
  lastName: string;
  email: string;
  organization: string | null;
  address: string | null;
  phone: string | null;
  linkedinUrl: string | null;
  cookiePreference: "necessary" | "all";
  directoryVisible: boolean;
  defaultDecisionVisibility: "public" | "private";
  allowDigestEmail: boolean;
  role: string;
}

interface AccessKey {
  id: string;
  label: string;
  prefix: string;
  key: string;
  lastUsedAt: string | null;
  createdAt: string;
}

interface LoginHistory {
  events: Array<{ method: "recovery_key" | "email_code"; createdAt: string }>;
  sessions: Array<{
    createdAt: string;
    lastSeenAt: string;
    userAgent: string | null;
    active: boolean;
  }>;
}

function errorMessage(caught: unknown, fallback: string) {
  return caught instanceof ApiClientError ? caught.message : fallback;
}

export function AccountPage() {
  const { user, loading, refresh } = useAuth();
  const userId = user?.id;
  const navigate = useNavigate();
  const [account, setAccount] = useState<Account | null>(null);
  const [keys, setKeys] = useState<AccessKey[]>([]);
  const [history, setHistory] = useState<LoginHistory | null>(null);
  const [loadError, setLoadError] = useState("");
  const [saveError, setSaveError] = useState("");
  const [imageError, setImageError] = useState("");
  const [recoveryKeyError, setRecoveryKeyError] = useState("");
  const [recoveryLoginDueAt, setRecoveryLoginDueAt] = useState<string | null>(
    null,
  );
  const [recoveryRequired, setRecoveryRequired] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [lifecycleError, setLifecycleError] = useState("");
  const [dirty, setDirty] = useState(false);
  const [lifecycleDialog, setLifecycleDialog] = useState<
    "suspend" | "delete" | null
  >(null);
  const [lifecycleBusy, setLifecycleBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [imageSaving, setImageSaving] = useState(false);
  const [recoveryKeySaving, setRecoveryKeySaving] = useState(false);
  const [imageMissing, setImageMissing] = useState(false);
  const [imageVersion, setImageVersion] = useState(0);
  const canManage = ["owner", "manager"].includes(user?.account.role ?? "");
  const lifecycleDialogRef = useDialogFocusTrap(
    lifecycleDialog !== null,
    () => {
      if (lifecycleBusy) return;
      setLifecycleError("");
      setLifecycleDialog(null);
    },
  );

  const load = useCallback(async () => {
    const value = await api<Account>("/api/account");
    setAccount(value);
    try {
      const result = await api<{
        items: AccessKey[];
        recoveryLoginDueAt: string | null;
        recoveryRequired: boolean;
      }>("/api/account/access-keys");
      setKeys(result.items);
      setRecoveryLoginDueAt(result.recoveryLoginDueAt);
      setRecoveryRequired(result.recoveryRequired);
    } catch (caught: unknown) {
      setRecoveryKeyError(
        errorMessage(caught, "Unable to load the emergency recovery key."),
      );
    }
    try {
      setHistory(await api<LoginHistory>("/api/account/login-history"));
    } catch (caught: unknown) {
      setHistoryError(errorMessage(caught, "Unable to load login activity."));
    }
  }, []);

  useEffect(() => {
    if (userId) {
      setLoadError("");
      void load().catch((caught: unknown) =>
        setLoadError(errorMessage(caught, "Unable to load account.")),
      );
    }
  }, [load, userId]);

  const retryLoad = () => {
    setLoadError("");
    void load().catch((caught: unknown) =>
      setLoadError(errorMessage(caught, "Unable to load account.")),
    );
  };

  if (!loading && !user) {
    return <Navigate to="/sign-in" replace state={{ from: "/account" }} />;
  }

  if (!account) {
    return (
      <div className="page-section account-page">
        <div className="container">
          {loadError ? (
            <div className="alert alert-error" role="alert">
              <p>{loadError}</p>
              <button
                className="button button-small button-secondary"
                type="button"
                onClick={retryLoad}
              >
                Try again
              </button>
            </div>
          ) : (
            <div className="loading-block" role="status">
              Loading account…
            </div>
          )}
        </div>
      </div>
    );
  }

  const recoveryKey = keys[0];

  const saveSettings = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setSaveError("");
    setSaving(true);
    const nextAccount = {
      ...account,
      firstName: String(form.get("firstName") ?? ""),
      lastName: String(form.get("lastName") ?? ""),
      organization: canManage
        ? String(form.get("organization") ?? "") || null
        : account.organization,
      address: canManage
        ? String(form.get("address") ?? "") || null
        : account.address,
      phone: String(form.get("phone") ?? "") || null,
      linkedinUrl: String(form.get("linkedinUrl") ?? "") || null,
      directoryVisible: canManage
        ? form.get("directoryVisible") === "on"
        : account.directoryVisible,
      defaultDecisionVisibility: canManage
        ? (String(
            form.get("defaultDecisionVisibility"),
          ) as Account["defaultDecisionVisibility"])
        : account.defaultDecisionVisibility,
      allowDigestEmail: canManage
        ? form.get("allowDigestEmail") === "on"
        : account.allowDigestEmail,
      cookiePreference: canManage
        ? (String(form.get("cookiePreference")) as Account["cookiePreference"])
        : account.cookiePreference,
    };

    try {
      const updates = [
        api("/api/account/profile", {
          method: "PATCH",
          body: JSON.stringify({
            firstName: nextAccount.firstName,
            lastName: nextAccount.lastName,
            organization: nextAccount.organization,
            address: nextAccount.address,
            phone: nextAccount.phone,
            linkedinUrl: nextAccount.linkedinUrl,
          }),
        }),
      ];
      if (canManage) {
        updates.push(
          api("/api/account/privacy", {
            method: "PATCH",
            body: JSON.stringify({
              directoryVisible: nextAccount.directoryVisible,
              defaultDecisionVisibility: nextAccount.defaultDecisionVisibility,
              allowDigestEmail: nextAccount.allowDigestEmail,
            }),
          }),
          api("/api/account/cookies", {
            method: "PATCH",
            body: JSON.stringify({ preference: nextAccount.cookiePreference }),
          }),
        );
      }
      await Promise.all(updates);
      setAccount(nextAccount);
      setDirty(false);
      void refresh().catch(() => undefined);
    } catch (caught: unknown) {
      setSaveError(errorMessage(caught, "Unable to save account changes."));
    } finally {
      setSaving(false);
    }
  };

  const replaceRecoveryKey = async () => {
    setRecoveryKeyError("");
    setRecoveryKeySaving(true);
    try {
      const replacement = await api<AccessKey>("/api/account/access-keys", {
        method: "POST",
        body: JSON.stringify({}),
      });
      setKeys([replacement]);
    } catch (caught: unknown) {
      setRecoveryKeyError(
        errorMessage(caught, "Unable to replace the emergency recovery key."),
      );
    } finally {
      setRecoveryKeySaving(false);
    }
  };

  const copyRecoveryKey = async (key: string) => {
    setRecoveryKeyError("");
    try {
      await navigator.clipboard.writeText(key);
    } catch {
      setRecoveryKeyError("Unable to copy the emergency recovery key.");
    }
  };

  const uploadImage = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    setImageError("");
    setImageSaving(true);
    try {
      await api("/api/account/profile-image", { method: "POST", body: form });
      setImageMissing(false);
      setImageVersion((value) => value + 1);
      notifyProfileImageChanged();
    } catch (caught: unknown) {
      setImageError(errorMessage(caught, "Unable to save the profile photo."));
    } finally {
      setImageSaving(false);
      event.target.value = "";
    }
  };

  return (
    <div className="page-section account-page">
      <div className="container account-container">
        <div className="page-heading account-heading">
          <h1>
            <FontAwesomeIcon icon={faGear} /> Account settings
          </h1>
          <span className="role-badge">{account.role}</span>
        </div>

        <nav className="account-section-nav" aria-label="Account sections">
          <a href="#profile">
            <FontAwesomeIcon icon={faUser} /> Profile
          </a>
          <a href="#security">
            <FontAwesomeIcon icon={faShieldHalved} /> Security
          </a>
          {canManage ? (
            <a href="#privacy">
              <FontAwesomeIcon icon={faEye} /> Privacy
            </a>
          ) : null}
          {user?.account.role === "owner" ? (
            <a href="#account-lifecycle">
              <FontAwesomeIcon icon={faGear} /> Account
            </a>
          ) : null}
        </nav>

        <form
          className="account-settings-form"
          onSubmit={saveSettings}
          onChange={(event) => {
            const form = new FormData(event.currentTarget);
            const profileChanged =
              String(form.get("firstName") ?? "") !== account.firstName ||
              String(form.get("lastName") ?? "") !== account.lastName ||
              String(form.get("phone") ?? "") !== (account.phone ?? "") ||
              String(form.get("linkedinUrl") ?? "") !==
                (account.linkedinUrl ?? "");
            const managedSettingsChanged =
              canManage &&
              (String(form.get("organization") ?? "") !==
                (account.organization ?? "") ||
                String(form.get("address") ?? "") !== (account.address ?? "") ||
                (form.get("directoryVisible") === "on") !==
                  account.directoryVisible ||
                String(form.get("defaultDecisionVisibility") ?? "") !==
                  account.defaultDecisionVisibility ||
                (form.get("allowDigestEmail") === "on") !==
                  account.allowDigestEmail ||
                String(form.get("cookiePreference") ?? "") !==
                  account.cookiePreference);
            setDirty(profileChanged || managedSettingsChanged);
            setSaveError("");
          }}
        >
          <section className="panel account-profile-card" id="profile">
            <div className="account-profile-header">
              <div className="account-photo-editor">
                <div className="account-photo" aria-hidden="true">
                  <FontAwesomeIcon icon={faUser} />
                  {!imageMissing ? (
                    <img
                      src={`/api/account/profile-image?v=${String(imageVersion)}`}
                      alt=""
                      onError={() => setImageMissing(true)}
                    />
                  ) : null}
                </div>
                <label className="button button-small button-ghost image-button">
                  <FontAwesomeIcon icon={faImage} />
                  {imageSaving ? "Uploading…" : "Change photo"}
                  <input
                    className="visually-hidden"
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    disabled={imageSaving}
                    onChange={(event) => void uploadImage(event)}
                  />
                </label>
                <span>PNG, JPG, or WebP</span>
                {imageError ? (
                  <span className="field-message field-error" role="alert">
                    {imageError}
                  </span>
                ) : null}
              </div>

              <div className="account-profile-fields">
                <div className="profile-title-row">
                  <div>
                    <h2>
                      <FontAwesomeIcon icon={faUser} /> {account.firstName}{" "}
                      {account.lastName}
                    </h2>
                    <p>{account.organization || "Independent account"}</p>
                  </div>
                  <span className="profile-role">{account.role}</span>
                </div>
                <div className="form-grid profile-name-grid">
                  <label>
                    First name
                    <input
                      name="firstName"
                      required
                      autoComplete="given-name"
                      defaultValue={account.firstName}
                    />
                  </label>
                  <label>
                    Last name
                    <input
                      name="lastName"
                      required
                      autoComplete="family-name"
                      defaultValue={account.lastName}
                    />
                  </label>
                  <label className="field-span">
                    Organization
                    <input
                      name="organization"
                      autoComplete="organization"
                      defaultValue={account.organization ?? ""}
                      disabled={!canManage}
                    />
                  </label>
                </div>
              </div>
            </div>

            <div className="account-contact-card">
              <div className="account-contact-heading">
                <h2>
                  <FontAwesomeIcon icon={faAddressCard} /> Contact information
                </h2>
              </div>
              <div className="form-grid account-contact-grid">
                <label className="field-span">
                  Email address
                  <input value={account.email} disabled />
                </label>
                <label className="field-span">
                  Mailing address
                  <textarea
                    name="address"
                    rows={3}
                    autoComplete="street-address"
                    defaultValue={account.address ?? ""}
                    disabled={!canManage}
                  />
                </label>
                <label>
                  Telephone
                  <input
                    name="phone"
                    type="tel"
                    autoComplete="tel"
                    defaultValue={account.phone ?? ""}
                  />
                </label>
                <label>
                  LinkedIn URL
                  <input
                    name="linkedinUrl"
                    type="url"
                    defaultValue={account.linkedinUrl ?? ""}
                    placeholder="https://www.linkedin.com/in/..."
                  />
                </label>
              </div>
            </div>
          </section>

          <section className="panel account-settings-group" id="security">
            <header>
              <span className="account-group-icon">
                <FontAwesomeIcon icon={faShieldHalved} />
              </span>
              <h2>Security</h2>
            </header>

            <div className="account-setting-row">
              <span className="setting-row-icon">
                <FontAwesomeIcon icon={faEnvelope} />
              </span>
              <div className="setting-row-copy">
                <strong>Login by email</strong>
                <span>Login codes are sent to this verified address.</span>
              </div>
              <strong className="setting-row-value">{account.email}</strong>
            </div>

            <div className="account-setting-row password-setting-row">
              <span className="setting-row-icon">
                <FontAwesomeIcon icon={faKey} />
              </span>
              <div className="setting-row-copy">
                <strong>Emergency recovery key</strong>
                <span>
                  Use this key to sign in at least once every 90 days.
                </span>
                <span
                  className={`password-status ${recoveryRequired ? "is-overdue" : ""}`}
                >
                  <FontAwesomeIcon icon={faClock} />
                  {recoveryRequired
                    ? "Recovery-key sign-in required now"
                    : recoveryLoginDueAt
                      ? `Next required by ${new Date(recoveryLoginDueAt).toLocaleDateString()}`
                      : "90-day clock starts when this key is created"}
                </span>
                {recoveryKeyError ? (
                  <span className="inline-field-message is-error" role="alert">
                    {recoveryKeyError}
                  </span>
                ) : null}
              </div>
              <div className="password-inline-panel">
                {recoveryKey ? (
                  <div className="password-inline-controls">
                    <button
                      type="button"
                      className="password-copy-target"
                      aria-label="Copy emergency recovery key"
                      title="Copy emergency recovery key"
                      onClick={() => void copyRecoveryKey(recoveryKey.key)}
                    >
                      <code className="password-value">{recoveryKey.key}</code>
                    </button>
                    <button
                      type="button"
                      className="icon-button"
                      aria-label="Replace emergency recovery key"
                      title="Replace emergency recovery key"
                      disabled={recoveryKeySaving}
                      onClick={() => void replaceRecoveryKey()}
                    >
                      <FontAwesomeIcon
                        icon={faRotate}
                        spin={recoveryKeySaving}
                      />
                    </button>
                  </div>
                ) : null}
              </div>
            </div>

            <details className="account-setting-disclosure">
              <summary>
                <span className="setting-row-icon">
                  <FontAwesomeIcon icon={faClock} />
                </span>
                <span className="setting-row-copy">
                  <strong>Login activity</strong>
                  <span>Review recent successful sign-ins and active use.</span>
                </span>
                <span className="setting-row-action">View login logs</span>
              </summary>
              <div className="account-disclosure-body login-history">
                {historyError ? (
                  <div
                    className="inline-operation-message alert-error"
                    role="alert"
                  >
                    {historyError}
                  </div>
                ) : history?.events.length ? (
                  history.events.map((event, index) => (
                    <div key={`${event.createdAt}-${String(index)}`}>
                      <strong>
                        {event.method === "recovery_key"
                          ? "Recovery key"
                          : "Email code"}
                      </strong>
                      <span>{new Date(event.createdAt).toLocaleString()}</span>
                    </div>
                  ))
                ) : (
                  <p>No successful login events are available.</p>
                )}
              </div>
            </details>
          </section>

          {canManage ? (
            <section className="panel account-settings-group" id="privacy">
              <header>
                <span className="account-group-icon">
                  <FontAwesomeIcon icon={faEye} />
                </span>
                <h2>Privacy</h2>
              </header>

              <div className="account-setting-row">
                <span className="setting-row-icon">
                  <FontAwesomeIcon icon={faDownload} />
                </span>
                <div className="setting-row-copy">
                  <strong>Export account data</strong>
                  <span>Download a portable JSON copy of your account.</span>
                </div>
                <a
                  className="button button-small button-ghost"
                  href="/api/account/export"
                >
                  Export data
                </a>
              </div>

              <label className="account-setting-row account-checkbox-row">
                <span className="setting-row-icon">
                  <FontAwesomeIcon icon={faEye} />
                </span>
                <span className="setting-row-copy">
                  <strong>Public directory</strong>
                  <span>Show this account in the public directory.</span>
                </span>
                <input
                  name="directoryVisible"
                  type="checkbox"
                  defaultChecked={account.directoryVisible}
                />
              </label>

              <label className="account-setting-row account-select-row">
                <span className="setting-row-icon">
                  <FontAwesomeIcon icon={faShieldHalved} />
                </span>
                <span className="setting-row-copy">
                  <strong>Default guide visibility</strong>
                  <span>Choose how newly created guides begin.</span>
                </span>
                <select
                  name="defaultDecisionVisibility"
                  defaultValue={account.defaultDecisionVisibility}
                >
                  <option value="private">Private</option>
                  <option value="public">Public</option>
                </select>
              </label>

              <label className="account-setting-row account-select-row">
                <span className="setting-row-icon">
                  <FontAwesomeIcon icon={faCookieBite} />
                </span>
                <span className="setting-row-copy">
                  <strong>Cookie preferences</strong>
                  <span>
                    StanceKit currently uses only the required session cookie.
                  </span>
                </span>
                <select
                  name="cookiePreference"
                  defaultValue={account.cookiePreference}
                >
                  <option value="necessary">Required cookies only</option>
                  <option value="all">Allow optional functional cookies</option>
                </select>
              </label>

              <label className="account-setting-row account-checkbox-row">
                <span className="setting-row-icon">
                  <FontAwesomeIcon icon={faEnvelope} />
                </span>
                <span className="setting-row-copy">
                  <strong>Account digest</strong>
                  <span>Allow periodic account summary emails.</span>
                </span>
                <input
                  name="allowDigestEmail"
                  type="checkbox"
                  defaultChecked={account.allowDigestEmail}
                />
              </label>
            </section>
          ) : null}

          {dirty || saveError ? (
            <div className="account-action-bar">
              {saveError ? (
                <span className="account-action-error" role="alert">
                  {saveError}
                </span>
              ) : null}
              {dirty ? (
                <div className="account-action-buttons">
                  <button
                    type="reset"
                    className="button button-ghost"
                    disabled={saving}
                    onClick={() => {
                      setDirty(false);
                      setSaveError("");
                    }}
                  >
                    Cancel
                  </button>
                  <button className="button" disabled={saving}>
                    {saving ? "Saving…" : "Save changes"}
                  </button>
                </div>
              ) : null}
            </div>
          ) : null}
        </form>

        {user?.account.role === "owner" ? (
          <section
            className="panel account-lifecycle-card"
            id="account-lifecycle"
          >
            <div className="account-lifecycle-heading">
              <span className="account-group-icon">
                <FontAwesomeIcon icon={faGear} />
              </span>
              <h2>Account</h2>
            </div>
            <div className="lifecycle-action warning-action">
              <span className="setting-row-icon">
                <FontAwesomeIcon icon={faPause} />
              </span>
              <div>
                <strong>Suspend account</strong>
                <span>
                  Hide the account and its data until the owner signs in again.
                </span>
              </div>
              <button
                type="button"
                className="button button-small button-ghost"
                onClick={() => {
                  setLifecycleError("");
                  setLifecycleDialog("suspend");
                }}
              >
                Suspend
              </button>
            </div>
            <div className="lifecycle-action danger-action">
              <span className="setting-row-icon">
                <FontAwesomeIcon icon={faTrashCan} />
              </span>
              <div>
                <strong>Delete account</strong>
                <span>
                  Permanently revoke access and remove the account from normal
                  use.
                </span>
              </div>
              <button
                className="button button-small button-danger"
                onClick={() => {
                  setLifecycleError("");
                  setLifecycleDialog("delete");
                }}
              >
                Delete account
              </button>
            </div>
          </section>
        ) : null}
      </div>

      {lifecycleDialog === "suspend" ? (
        <div
          className="account-dialog-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !lifecycleBusy) {
              setLifecycleError("");
              setLifecycleDialog(null);
            }
          }}
        >
          <section
            ref={lifecycleDialogRef}
            className="account-dialog warning-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="suspend-account-title"
            tabIndex={-1}
          >
            <span className="dialog-warning-icon">
              <FontAwesomeIcon icon={faPause} />
            </span>
            <h2 id="suspend-account-title">Suspend this account?</h2>
            <div className="dialog-caution">
              <p>
                Everyone will be signed out. Public guides and member access
                stay hidden until the owner signs in again.
              </p>
            </div>
            <form
              onSubmit={async (event) => {
                event.preventDefault();
                setLifecycleError("");
                setLifecycleBusy(true);
                try {
                  await api("/api/account/suspend", {
                    method: "POST",
                    body: JSON.stringify({}),
                  });
                  navigate("/");
                } catch (caught: unknown) {
                  setLifecycleError(
                    errorMessage(caught, "Unable to suspend account."),
                  );
                  setLifecycleBusy(false);
                }
              }}
            >
              {lifecycleError ? (
                <div
                  className="inline-operation-message alert-error"
                  role="alert"
                >
                  {lifecycleError}
                </div>
              ) : null}
              <div className="dialog-actions">
                <button
                  type="button"
                  className="button button-ghost"
                  disabled={lifecycleBusy}
                  onClick={() => {
                    setLifecycleError("");
                    setLifecycleDialog(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  className="button button-warning"
                  disabled={lifecycleBusy}
                >
                  {lifecycleBusy ? "Suspending…" : "Suspend account"}
                </button>
              </div>
            </form>
          </section>
        </div>
      ) : null}

      {lifecycleDialog === "delete" ? (
        <div
          className="account-dialog-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !lifecycleBusy) {
              setLifecycleError("");
              setLifecycleDialog(null);
            }
          }}
        >
          <section
            ref={lifecycleDialogRef}
            className="account-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-account-title"
            tabIndex={-1}
          >
            <span className="dialog-danger-icon">
              <FontAwesomeIcon icon={faTrashCan} />
            </span>
            <h2 id="delete-account-title">Delete this account?</h2>
            <div className="dialog-caution dialog-caution-danger">
              <p>
                <strong>This cannot be undone.</strong> Sign-in, profile, and
                contact data will be permanently removed. Records retained for
                integrity are anonymized.
              </p>
            </div>
            <form
              onSubmit={async (event) => {
                event.preventDefault();
                const value = new FormData(event.currentTarget).get(
                  "confirmation",
                );
                setLifecycleError("");
                setLifecycleBusy(true);
                try {
                  await api("/api/account", {
                    method: "DELETE",
                    body: JSON.stringify({ confirmation: value }),
                  });
                  navigate("/");
                } catch (caught: unknown) {
                  setLifecycleError(
                    errorMessage(caught, "Unable to delete account."),
                  );
                  setLifecycleBusy(false);
                }
              }}
            >
              <label>
                <span>
                  Type <strong>"delete"</strong> to confirm
                </span>
                <input
                  name="confirmation"
                  required
                  pattern="delete"
                  autoFocus
                  autoComplete="off"
                />
              </label>
              {lifecycleError ? (
                <div
                  className="inline-operation-message alert-error"
                  role="alert"
                >
                  {lifecycleError}
                </div>
              ) : null}
              <div className="dialog-actions">
                <button
                  type="button"
                  className="button button-ghost"
                  disabled={lifecycleBusy}
                  onClick={() => {
                    setLifecycleError("");
                    setLifecycleDialog(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  className="button button-danger"
                  disabled={lifecycleBusy}
                >
                  {lifecycleBusy ? "Deleting…" : "Delete account permanently"}
                </button>
              </div>
            </form>
          </section>
        </div>
      ) : null}
    </div>
  );
}
